"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

type Props = {
  locale: "fr" | "en";
  /** Durée minimale d'affichage du loader (ms) */
  minDuration?: number;
};

/**
 * Loader fullscreen affiché à l'arrivée sur la page.
 * Style "Osmo" : logo centré sur fond noir, texte discret en bas, fade-out doux.
 * Verrouille le scroll pendant son affichage et le réinitialise au top en sortant
 * pour que le hero scroll-scrub démarre toujours à progress 0.
 */
export default function Loader({ locale, minDuration = 2200 }: Props) {
  const [isVisible, setIsVisible] = useState(true);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t = setTimeout(
      () => setIsVisible(false),
      prefersReduced ? 400 : minDuration
    );
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [minDuration, prefersReduced]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        document.body.style.overflow = "";
        window.scrollTo(0, 0);
      }}
    >
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          {/* Logo centré, fade-in + scale subtil */}
          <motion.div
            initial={prefersReduced ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
            animate={prefersReduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Image
              src="/WC-Menu-logo.png"
              alt="Welconcept"
              width={320}
              height={72}
              priority
              className="h-14 md:h-16 w-auto"
            />
          </motion.div>

          {/* Texte discret en bas (style "HOLD TIGHT" de Osmo) */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-white/60 font-body"
          >
            {locale === "fr" ? "Bienvenue" : "Welcome"}
          </motion.p>

          {/* Indicateur de progression subtil — barre fine animée */}
          {!prefersReduced && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: (minDuration - 200) / 1000, ease: "easeInOut" }}
              style={{ transformOrigin: "left" }}
              className="absolute bottom-10 left-1/2 -translate-x-1/2 h-px w-32 bg-white/30"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
